// The moves a person makes on a whole campaign, built from the API's moves on
// its countries. The API pauses and resumes a country campaign
// (`/campaigns/{id}/pause|resume`); a campaign-wide pause is that call on
// every country that is sending, and each country's answer is its own — one
// country refusing does not undo the others, and the result says which.

import { pauseCampaign, resumeCampaign } from "@/lib/api";
import type { GroupCountry } from "@/lib/api";
import { messageOf } from "@/pages/campaigns/model";

export interface FanOut {
  done: string[];
  refused: { country: string; message: string }[];
}

async function each(
  countries: GroupCountry[],
  move: (campaignId: number) => Promise<unknown>,
): Promise<FanOut> {
  const answers = await Promise.all(
    countries.map(async (row) => {
      try {
        await move(row.campaign_id);
        return { country: row.country, message: null as string | null };
      } catch (raised: unknown) {
        return { country: row.country, message: messageOf(raised) };
      }
    }),
  );
  return {
    done: answers.filter((answer) => answer.message === null).map((answer) => answer.country),
    refused: answers
      .filter((answer): answer is { country: string; message: string } => answer.message !== null)
      .map((answer) => ({ country: answer.country, message: answer.message })),
  };
}

export function sendingOf(countries: GroupCountry[]): GroupCountry[] {
  return countries.filter((row) => row.state === "sending");
}

export function pausedOf(countries: GroupCountry[]): GroupCountry[] {
  return countries.filter((row) => row.state === "paused");
}

export function pauseAll(countries: GroupCountry[]): Promise<FanOut> {
  return each(sendingOf(countries), pauseCampaign);
}

export function resumeAll(countries: GroupCountry[]): Promise<FanOut> {
  return each(pausedOf(countries), resumeCampaign);
}

/** Throws when nothing moved, so the dialog stays open with the reason. */
export function assertMoved(result: FanOut): FanOut {
  if (result.done.length === 0 && result.refused.length > 0) {
    throw new Error(result.refused.map((row) => `${row.country}: ${row.message}`).join(" · "));
  }
  return result;
}
